import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
    BookOpen, Play, CheckCircle2, AlertTriangle, Lightbulb,
    List, Info, HelpCircle, GraduationCap
} from 'lucide-react';

// Markdown için özel bileşenler
const MarkdownComponents = {
    // Tablolar
    table: ({ children }) => (
        <div className="overflow-x-auto my-4">
            <table className="w-full text-sm border-collapse">
                {children}
            </table>
        </div>
    ),
    thead: ({ children }) => (
        <thead className="bg-secondary/50 border-b border-border">
            {children}
        </thead>
    ),
    tbody: ({ children }) => (
        <tbody className="divide-y divide-border/50">
            {children}
        </tbody>
    ),
    tr: ({ children }) => (
        <tr className="hover:bg-secondary/30 transition-colors">
            {children}
        </tr>
    ),
    th: ({ children }) => (
        <th className="px-4 py-3 text-left font-semibold text-foreground border border-border/50">
            {children}
        </th>
    ),
    td: ({ children }) => (
        <td className="px-4 py-3 text-muted-foreground border border-border/30">
            {children}
        </td>
    ),
    // Başlıklar
    h1: ({ children }) => (
        <h1 className="text-2xl font-bold text-foreground mt-6 mb-4">{children}</h1>
    ),
    h2: ({ children }) => (
        <h2 className="text-xl font-bold text-foreground mt-5 mb-3">{children}</h2>
    ),
    h3: ({ children }) => (
        <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">{children}</h3>
    ),
    h4: ({ children }) => (
        <h4 className="text-base font-semibold text-foreground mt-3 mb-2">{children}</h4>
    ),
    // Paragraflar
    p: ({ children }) => (
        <p className="text-muted-foreground leading-relaxed mb-3">{children}</p>
    ),
    // Listeler
    ul: ({ children }) => (
        <ul className="list-disc list-inside space-y-1 mb-3 ml-4 text-muted-foreground">
            {children}
        </ul>
    ),
    ol: ({ children }) => (
        <ol className="list-decimal list-inside space-y-1 mb-3 ml-4 text-muted-foreground">
            {children}
        </ol>
    ),
    li: ({ children }) => (
        <li className="text-muted-foreground">{children}</li>
    ),
    // Bold, italic
    strong: ({ children }) => (
        <strong className="font-semibold text-foreground">{children}</strong>
    ),
    em: ({ children }) => (
        <em className="italic text-primary/90">{children}</em>
    ),
    // Kod
    code: ({ inline, children }) => {
        if (inline) {
            return (
                <code className="px-1.5 py-0.5 rounded bg-secondary text-primary text-sm font-mono">
                    {children}
                </code>
            );
        }
        return (
            <pre className="p-4 rounded-lg bg-secondary/50 border border-border overflow-x-auto my-4">
                <code className="text-sm font-mono text-foreground">
                    {children}
                </code>
            </pre>
        );
    },
    // Alıntı
    blockquote: ({ children }) => (
        <blockquote className="border-l-4 border-primary pl-4 py-2 my-4 bg-primary/5 rounded-r">
            {children}
        </blockquote>
    ),
    // Yatay çizgi
    hr: () => (
        <hr className="my-6 border-t border-border" />
    ),
    // Link
    a: ({ href, children }) => (
        <a 
            href={href} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-primary hover:underline"
        >
            {children}
        </a>
    ),
};

// Markdown içeriği render et
const MarkdownContent = ({ content }) => {
    if (!content) return null;
    
    return (
        <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={MarkdownComponents}
        >
            {content}
        </ReactMarkdown>
    );
};

const ContentBlockRenderer = ({ block }) => {
    if (!block) return null;
    
    switch (block.type) {
        case 'info_box':
            return (
                <div className="p-5 rounded-xl bg-primary/10 border border-primary/30">
                    <h4 className="font-semibold text-primary mb-3 flex items-center gap-2">
                        <Info className="w-5 h-5" />
                        {block.title}
                    </h4>
                    <div className="text-sm">
                        <MarkdownContent content={block.content} />
                    </div>
                </div>
            );
            
        case 'text': {
            const isLongDoc = (block.content || '').length > 1500;
            return (
                <div className={isLongDoc ? 'max-w-3xl mx-auto' : 'space-y-3'}>
                    {block.title && !isLongDoc && (
                        <h4 className="text-lg font-semibold text-foreground">{block.title}</h4>
                    )}
                    <div className={isLongDoc
                        ? 'text-[15px] leading-[1.85] text-foreground/90 space-y-0 [&_h2]:text-base [&_h2]:font-bold [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:pb-1 [&_h2]:border-b [&_h2]:border-border [&_h3]:text-[15px] [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:mb-4 [&_p]:text-foreground/80 [&_strong]:text-foreground [&_hr]:my-8'
                        : 'text-base leading-relaxed'
                    }>
                        <MarkdownContent content={block.content} />
                    </div>
                </div>
            );
        }
            
        case 'callout':
        case 'warning':
            const isWarning = block.style === 'warning' || block.type === 'warning';
            return (
                <div className={`p-4 rounded-lg border ${isWarning ? 'bg-amber-500/10 border-amber-500/30' : 'bg-blue-500/10 border-blue-500/30'}`}>
                    <div className="flex items-start gap-3">
                        <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isWarning ? 'text-amber-400' : 'text-blue-400'}`} />
                        <div className="flex-1">
                            <h4 className={`font-medium mb-1 ${isWarning ? 'text-amber-400' : 'text-blue-400'}`}>
                                {block.title}
                            </h4>
                            <div className="text-sm">
                                <MarkdownContent content={block.content} />
                            </div>
                        </div>
                    </div>
                </div>
            );
            
        case 'key_points':
        case 'list':
            const items = block.items || [];
            return (
                <div className="space-y-3">
                    <h4 className="font-medium flex items-center gap-2">
                        {block.type === 'key_points' ? (
                            <Lightbulb className="w-5 h-5 text-amber-400" />
                        ) : (
                            <List className="w-5 h-5 text-primary" />
                        )}
                        {block.title}
                    </h4>
                    <ul className="space-y-2">
                        {items.map((item, idx) => {
                            const itemText = typeof item === 'string' ? item : (item.text_tr || item.text || '');
                            return (
                                <li key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50 border border-border">
                                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm">
                                        <MarkdownContent content={itemText} />
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            );
            
        case 'example':
        case 'scenario':
            return (
                <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
                    <div className="flex items-start gap-3">
                        <BookOpen className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                            <h4 className="font-medium text-purple-400 mb-2">{block.title || 'Örnek'}</h4>
                            <div className="text-sm">
                                <MarkdownContent content={block.content} />
                            </div>
                        </div>
                    </div>
                </div>
            );
            
        case 'table':
            const tableData = block.data || {};
            const headers = tableData.headers || [];
            const rows = tableData.rows || [];
            return (
                <div className="rounded-lg border border-border overflow-hidden">
                    <div className="bg-secondary/50 px-4 py-2 border-b border-border">
                        <h4 className="font-medium text-sm">{block.title || 'Tablo'}</h4>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            {headers.length > 0 && (
                                <thead className="bg-secondary/30">
                                    <tr>
                                        {headers.map((h, i) => (
                                            <th key={i} className="px-4 py-2 text-left font-medium border-b border-border">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                            )}
                            <tbody>
                                {rows.map((row, ri) => (
                                    <tr key={ri} className="border-b border-border/50 last:border-0">
                                        {row.map((cell, ci) => (
                                            <td key={ci} className="px-4 py-2">
                                                <MarkdownContent content={cell} />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            );
            
        case 'quiz_intro':
            return (
                <div className="p-5 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30 text-center">
                    <GraduationCap className="w-10 h-10 mx-auto text-primary mb-3" />
                    <h4 className="font-semibold text-lg mb-2">{block.title || 'Bilgini Test Et!'}</h4>
                    <div className="text-sm text-muted-foreground">
                        <MarkdownContent content={block.content} />
                    </div>
                </div>
            );
            
        case 'summary':
            const summaryItems = block.items || [];
            return (
                <div className="p-5 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/5 border border-green-500/30">
                    <h4 className="font-semibold text-lg mb-4 flex items-center gap-2 text-green-400">
                        <CheckCircle2 className="w-5 h-5" />
                        {block.title || 'Özet'}
                    </h4>
                    {summaryItems.length > 0 ? (
                        <ul className="space-y-2">
                            {summaryItems.map((item, idx) => {
                                const itemText = typeof item === 'string' ? item : (item.text_tr || item.text || JSON.stringify(item));
                                return (
                                    <li key={idx} className="flex items-start gap-3 text-sm">
                                        <span className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-medium text-green-400">
                                            {idx + 1}
                                        </span>
                                        <span className="text-muted-foreground">
                                            <MarkdownContent content={itemText} />
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : block.content ? (
                        <div className="text-sm text-muted-foreground">
                            <MarkdownContent content={block.content} />
                        </div>
                    ) : null}
                </div>
            );
            
        case 'inline_quiz':
            return (
                <div className="p-5 rounded-xl bg-gradient-to-br from-amber-500/10 to-orange-500/5 border border-amber-500/30">
                    <h4 className="font-semibold text-lg mb-3 flex items-center gap-2 text-amber-400">
                        <HelpCircle className="w-5 h-5" />
                        {block.title || 'Hızlı Kontrol'}
                    </h4>
                    <div className="text-sm text-muted-foreground">
                        <MarkdownContent content={block.content} />
                    </div>
                </div>
            );
            
        case 'image':
            const imageUrl = block.media_url || block.image_url || block.url;
            return (
                <div className="space-y-3">
                    {block.title && (
                        <h4 className="font-medium">{block.title}</h4>
                    )}
                    {imageUrl && (
                        <div className="rounded-lg overflow-hidden border border-border bg-secondary/30">
                            <img 
                                src={imageUrl.startsWith('http') ? imageUrl : `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`}
                                alt={block.title || block.alt || 'Görsel'}
                                className="w-full h-auto max-h-[500px] object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => window.open(imageUrl.startsWith('http') ? imageUrl : `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`, '_blank')}
                            />
                        </div>
                    )}
                    {block.caption && (
                        <p className="text-sm text-muted-foreground text-center italic">{block.caption}</p>
                    )}
                </div>
            );
            
        case 'video':
            const videoUrl = block.media_url || block.video_url || block.url;
            const isYoutube = videoUrl && (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be'));
            const getYoutubeEmbedUrl = (url) => {
                if (!url) return null;
                let videoId = null;
                if (url.includes('youtube.com/watch')) {
                    videoId = new URL(url).searchParams.get('v');
                } else if (url.includes('youtu.be/')) {
                    videoId = url.split('youtu.be/')[1]?.split('?')[0];
                } else if (url.includes('youtube.com/embed/')) {
                    videoId = url.split('embed/')[1]?.split('?')[0];
                }
                return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
            };
            
            return (
                <div className="space-y-3">
                    {block.title && (
                        <h4 className="font-medium flex items-center gap-2">
                            <Play className="w-5 h-5 text-red-500" />
                            {block.title}
                        </h4>
                    )}
                    <div className="rounded-lg overflow-hidden border border-border bg-black aspect-video">
                        {isYoutube ? (
                            <iframe
                                src={getYoutubeEmbedUrl(videoUrl)}
                                title={block.title || 'Video'}
                                className="w-full h-full"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        ) : videoUrl ? (
                            <video 
                                controls 
                                className="w-full h-full"
                                poster={block.thumbnail || block.poster}
                            >
                                <source src={videoUrl.startsWith('http') ? videoUrl : `${process.env.REACT_APP_BACKEND_URL}${videoUrl}`} type="video/mp4" />
                                Tarayıcınız video oynatmayı desteklemiyor.
                            </video>
                        ) : (
                            <div className="flex items-center justify-center h-full text-muted-foreground">
                                <Play className="w-12 h-12" />
                            </div>
                        )}
                    </div>
                    {block.caption && (
                        <p className="text-sm text-muted-foreground text-center italic">{block.caption}</p>
                    )}
                </div>
            );
            
        case 'media':
            const mediaUrl = block.media_url || block.url;
            const isVideoMedia = mediaUrl && (mediaUrl.includes('.mp4') || mediaUrl.includes('.webm') || mediaUrl.includes('youtube') || mediaUrl.includes('youtu.be'));
            if (isVideoMedia) {
                return <ContentBlockRenderer block={{ ...block, type: 'video' }} />;
            }
            return <ContentBlockRenderer block={{ ...block, type: 'image' }} />;
            
        default:
            // Default: Markdown desteği ile içerik göster
            return (
                <div className="p-4 rounded-lg bg-secondary/30 border border-border">
                    {block.title && <h4 className="font-medium mb-2">{block.title}</h4>}
                    <div className="text-sm">
                        <MarkdownContent content={block.content} />
                    </div>
                </div>
            );
    }
};

export default ContentBlockRenderer;
